/**
 * The one line a phone has room for.
 *
 * `Standings` is 110px of a 584px column and the second biggest thing in it,
 * and most of what it says is already on the screen: three of its four rows
 * repeat the ring, and the fourth - your own - is in the top bar, where
 * `TableTopBar` already draws `myScore`. What it uniquely carries is the
 * RANKING, plus a per-seat tally in the games that declare one.
 *
 * So on a phone it folds to what only it can say: where you stand, and who is
 * ahead. See `docs/phone-column-plan.md` for the measurements.
 */

/** One row of the table, whether a chair or a partnership. */
export interface StandingRow {
  key: string;
  name: string;
  score: number | null;
  /** Tricks won against tricks bid, already written out. */
  tally: string | null;
  /** Yours, or your side's. */
  you: boolean;
}

export interface StandingSummary {
  /** 1-based, and shared on a tie: two seats level on top are both first. */
  position: number;
  of: number;
  /** The row at the top, which may be yours. */
  leader: StandingRow;
  /** The row below the leader, which is what to name when the leader is you. */
  behind: StandingRow | null;
  /**
   * Whether somebody else is level with you.
   *
   * "You lead" is a claim, and at a poker table everybody starts level: three
   * seats on 1500 chips would each be told they were leading. Sharing the place
   * is the ordinary state at the start of every game, so it has to be sayable.
   */
  shared: boolean;
}

/**
 * Where you stand, given rows already in standings order.
 *
 * Null when there is nothing to stand on: no scores anywhere, or no row of
 * your own, which is what a spectator sees. The caller draws the full table in
 * that case rather than a summary of nothing.
 */
export function summarise(rows: readonly StandingRow[], lowIsBetter = false): StandingSummary | null {
  if (rows.length === 0) return null;

  const mine = rows.find((row) => row.you);
  if (!mine) return null;

  let scored = false;
  for (const row of rows) {
    if (row.score !== null) scored = true;
  }
  if (!scored) return null;

  /*
   * Counted rather than indexed, so a tie is a tie.
   *
   * Two players level at the top are both first, and the one who happens to
   * sit in the later chair should not be told they are second.
   */
  let ahead = 0;
  let shared = false;
  for (const row of rows) {
    if (row === mine || row.score === null || mine.score === null) continue;
    if (lowIsBetter ? row.score < mine.score : row.score > mine.score) ahead += 1;
    else if (row.score === mine.score) shared = true;
  }

  return {
    position: ahead + 1,
    of: rows.length,
    leader: rows[0] as StandingRow,
    behind: rows.length > 1 ? (rows[1] as StandingRow) : null,
    shared,
  };
}

/**
 * "1st", "2nd", "3rd", "4th" - and "11th" rather than "11st".
 *
 * The teens are the whole reason this is not a lookup on the last digit, and a
 * table can reach eleven at the free tier's upgraded seat count.
 */
export function ordinal(position: number): string {
  const tens = position % 100;
  if (tens >= 11 && tens <= 13) return `${String(position)}th`;

  switch (position % 10) {
    case 1:
      return `${String(position)}st`;
    case 2:
      return `${String(position)}nd`;
    case 3:
      return `${String(position)}rd`;
    default:
      return `${String(position)}th`;
  }
}
